import React from 'react';
import { DashboardIcon, DashboardIconType } from '../../components/ui/DashboardIcon';
import { EmptyState } from '../../components/ui/EmptyState';
import { PanelHeader } from '../../components/ui/PanelHeader';
import {
  getSystemHealth,
  SystemHealthMetric,
  SystemHealthResponse,
  SystemHealthService,
} from '../../services/system';

export function SystemHealth() {
  const [health, setHealth] = React.useState<SystemHealthResponse | null>(null);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    let isActive = true;

    getSystemHealth()
      .then((response) => {
        if (isActive) setHealth(response);
      })
      .catch((requestError) => {
        if (isActive) {
          setError(requestError instanceof Error ? requestError.message : 'Unable to load system health');
        }
      });

    return () => {
      isActive = false;
    };
  }, []);

  if (error) {
    return (
      <section className="system-health-panel empty">
        <EmptyState title="Unable to load system health" message={error} />
      </section>
    );
  }

  if (!health) {
    return (
      <section className="system-health-panel empty">
        <EmptyState title="Loading system health" message="Fetching live platform metrics and service statuses." />
      </section>
    );
  }

  const healthStats = getHealthStats(health);
  const services = getHealthServices(health);
  const overallOperational = isOperational(health.status);

  return (
    <div className="system-health-page">
      <section className="system-health-stats" aria-label="System health metrics">
        {healthStats.map((stat) => (
          <article className="system-health-stat" key={stat.label}>
            <div className="system-health-stat-top">
              <span className={`system-health-icon ${stat.tone}`}>
                <DashboardIcon type={stat.icon} size={24} />
              </span>
              <span className={`system-health-delta ${stat.trend}`}>
                <span aria-hidden="true">{stat.trend === 'up' ? '^' : 'v'}</span>
                {stat.delta}
              </span>
            </div>
            <strong>{stat.value}</strong>
            <span>{stat.label}</span>
          </article>
        ))}
      </section>

      <section className="system-health-panel">
        <PanelHeader
          actions={(
            <span className={`system-health-status${overallOperational ? '' : ' issue'}`}>
              <i aria-hidden="true" />
              {overallOperational ? 'All systems operational' : formatStatus(health.status)}
            </span>
          )}
          className="system-health-panel-head"
          title="Service status"
        />

        <div className="system-health-services">
          {services.map((service) => {
            const operational = isOperational(service.status);
            const unknown = service.status.toLowerCase() === 'unknown';
            return (
            <article className="system-health-service" key={service.name}>
              <span className={`system-health-icon ${operational ? 'green' : unknown ? 'muted' : 'red'}`}>
                <DashboardIcon type={service.name === 'PostgreSQL' ? 'database' : operational ? 'pulse' : 'warning'} size={24} />
              </span>
              <div>
                <strong>{service.name}</strong>
                <span>{service.detail}</span>
              </div>
              <span className={`system-health-status${operational ? '' : unknown ? ' unknown' : ' issue'}`}>
                <i aria-hidden="true" />
                {formatStatus(service.status)}
              </span>
            </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function getHealthServices(health: SystemHealthResponse): SystemHealthService[] {
  const includesPostgres = health.services.some((service) => /postgres(?:ql)?/i.test(service.name));

  if (includesPostgres) {
    return health.services;
  }

  const postgres = health.postgres_health;
  return [
    ...health.services,
    {
      name: 'PostgreSQL',
      status: postgres?.status ?? 'unknown',
      detail: postgres?.detail
        ?? (typeof postgres?.latency_ms === 'number' ? `${postgres.latency_ms}ms response` : 'No health data reported'),
    },
  ];
}

function getHealthStats(health: SystemHealthResponse) {
  return [
    createStat('API uptime', health.metrics.api_uptime, 'green', 'pulse', false),
    createStat('Avg response', health.metrics.average_response_time, 'blue', 'response', true),
    createStat('Active sessions', health.metrics.active_sessions, 'purple', 'sessions', false),
    createStat('Error rate', health.metrics.error_rate, 'red', 'warning', true),
  ];
}

function createStat(
  label: string,
  metric: SystemHealthMetric,
  tone: string,
  icon: DashboardIconType,
  lowerIsBetter: boolean,
) {
  const improving = lowerIsBetter ? metric.change <= 0 : metric.change >= 0;
  return {
    label,
    value: formatMetric(metric),
    delta: `${metric.change >= 0 ? '+' : ''}${metric.change}`,
    trend: improving ? 'up' : 'down',
    tone,
    icon,
  };
}

function formatMetric(metric: SystemHealthMetric) {
  if (metric.unit === 'percent') return `${metric.value}%`;
  if (metric.unit === 'ms') return `${metric.value}ms`;
  return metric.value.toLocaleString();
}

function isOperational(status: string) {
  return status.toLowerCase() === 'operational' || status.toLowerCase() === 'healthy';
}

function formatStatus(status: string) {
  return status.replace(/_/g, ' ');
}
